import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import {
  getStopContactData,
  deleteStopContact,
} from "../../../../../services/stopContact";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { useState, useEffect, useRef } from "react";
import { EditStopContactsRow } from "./EditStopContactRow";
import { columns } from "./columns";
import SbuxLoader from "../../../../../components/SbuxLoader";
import {
  getStopContactDataSelector,
  getStopContactStatusSelector,
} from "../../../../../selectors/stopContactSelector";
import { logMessage } from "../../../../../utils/amplifyLogger";
import { getConnectedInstanceSelector } from "../../../../../selectors/userInstanceSelector";
import { subOnProcessedContactEvent } from "../../../../../graphql/api";
import { OPERATION, TABLE_DEFAULT_PAGE_SIZE } from "../../../../../constants";
import {
  insertContactEvent,
  updateContactEvent,
  deleteContactEvent,
  updateCheckStatus,
  updateCheckStatusForRowsInPage,
  sortDataset,
  unCheckAll,
} from "../../../../../slices/stopContactSlice";
import { mapContactEvent, StopContactRow } from "./util";
import { useDebounce } from "../../../../../utils/commonUtil";
import { isNotEmptyString } from "../../../../../utils/stringUtil";
import {
  searchDataset,
  RowSelectionStrategy,
  SelectionStrategy,
} from "../../../../../utils/tableUtil";

interface Subscription {
  unsubscribe: () => void;
}

interface ContactEvent {
  initiationMethod?: string;
  __operation?: string;
  initialContactId?: string;
  [key: string]: unknown;
}

interface ContactEventData {
  data?: {
    onProcessedContactEvent?: ContactEvent;
  };
}

const paginationConfig = {
  rowsPerPage: TABLE_DEFAULT_PAGE_SIZE,
  page: 0,
};

const expectedInitialMethod = "CALLBACK";

const StopContactTable = () => {
  const dispatch = useAppDispatch();
  const [dataset, setDataset] = useState<StopContactRow[]>([]);
  const [selectedItems, setSelectedItems] = useState<StopContactRow[]>([]);
  const [pagination, setPagination] = useState(paginationConfig);
  const [selectionStrategy, setSelectionStrategy] = useState<SelectionStrategy>(
    {
      strategy: RowSelectionStrategy.BY_PAGE,
      mapping: new Map([[0, false]]),
    },
  );
  const [searchText, setSearchText] = useState<string | undefined>(undefined);

  const connectedInstance = useAppSelector(getConnectedInstanceSelector);
  const stopContactData = useAppSelector(getStopContactDataSelector);
  const loadingStatus = useAppSelector(getStopContactStatusSelector);
  const subs = useRef<Subscription[]>([]);
  const subTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStopContactData = async () => {
    await dispatch(getStopContactData());
  };

  const resetSelectionStrategy = (
    newStrategy?: number,
    keepMapping = false,
  ) => {
    setSelectionStrategy({
      strategy: newStrategy ?? selectionStrategy.strategy,
      mapping: keepMapping ? selectionStrategy.mapping : new Map(),
    });
  };

  const initialize = () => {
    subs.current.forEach((sub) => sub.unsubscribe());
    subs.current = [];

    fetchStopContactData();
    setDataset([]);
    setSelectedItems([]);
    setSearchText(undefined);
    resetSelectionStrategy();
    setPagination(paginationConfig);

    subTimerRef.current = setTimeout(() => {
      subTimerRef.current = null;
      if (!connectedInstance) return;

      const onProcessSub = subOnProcessedContactEvent(
        connectedInstance.instanceId,
      ).subscribe(
        (data: ContactEventData) => handleSubscription(data),
        (err: unknown) =>
          logMessage(
            `Stop Contact `,
            `Something went wrong in Subscription API => ${String(err)}`,
            `error`,
          ),
      );

      subs.current = [onProcessSub];
    }, 0);
  };

  useEffect(() => {
    initialize();

    return () => {
      if (subTimerRef.current !== null) clearTimeout(subTimerRef.current);
      subs.current.forEach((sub) => sub.unsubscribe());
    };
  }, [connectedInstance?.instanceId]);

  useEffect(() => {
    const contactEvents = (stopContactData ?? []).slice() as StopContactRow[];

    if (isNotEmptyString(searchText ?? "")) {
      const filtered = searchDataset(
        columns,
        contactEvents,
        searchText ?? "",
      ) as StopContactRow[];
      setDataset(filtered);
    } else {
      setDataset(contactEvents);
    }

    if (!contactEvents.length) {
      resetSelectionStrategy(RowSelectionStrategy.DISABLED);
    } else {
      resetSelectionStrategy(RowSelectionStrategy.BY_PAGE, true);
    }

    const selected = contactEvents.filter((item) => item.isChecked);
    setSelectedItems(selected);
  }, [stopContactData, searchText]);

  const handleSubscription = (data: ContactEventData) => {
    const contactEvent = data?.data?.onProcessedContactEvent;

    if (
      !contactEvent ||
      contactEvent.initiationMethod !== expectedInitialMethod
    ) {
      return;
    }

    const operation = contactEvent.__operation;
    switch (operation) {
      case OPERATION.CREATE:
        dispatch(
          insertContactEvent(
            contactEvent as Parameters<typeof insertContactEvent>[0],
          ),
        );
        break;
      case OPERATION.UPDATE: {
        dispatch(
          updateContactEvent(
            contactEvent as Parameters<typeof updateContactEvent>[0],
          ),
        );
        updateSelectionList(contactEvent);
        break;
      }
      case OPERATION.DELETE: {
        dispatch(
          deleteContactEvent(
            contactEvent as Parameters<typeof deleteContactEvent>[0],
          ),
        );
        updateSelectionList(contactEvent);
        break;
      }
    }
  };

  const updateSelectionList = (contactEvent: ContactEvent) => {
    const { initialContactId } = contactEvent;
    const filtered = selectedItems.filter(
      (item) => item.initialContactId !== initialContactId,
    );
    if (filtered.length !== selectedItems.length) {
      setSelectedItems(filtered);
    }
  };

  const handleSelectAllChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    const mapping = new Map(selectionStrategy.mapping?.entries() ?? []);
    mapping.set(pagination.page, checked);
    setSelectionStrategy({
      strategy: selectionStrategy.strategy,
      mapping,
    });

    await dispatch(
      updateCheckStatusForRowsInPage({
        pagination,
        isChecked: checked,
      }),
    );
  };

  const handleDelete = async () => {
    const payload = selectedItems.map((item) => mapContactEvent(item));
    await dispatch(deleteStopContact(payload));
    setSelectedItems([]);
    resetSelectionStrategy();
    setPagination({ ...pagination, page: 0 });
  };

  const handleEditRowClick = (row: StopContactRow) => {
    dispatch(updateCheckStatus(row as Parameters<typeof updateCheckStatus>[0]));
  };

  const handleRowsPerPageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination({ ...pagination, rowsPerPage: Number(event.target.value) });
    resetSelectionStrategy();
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    pageNumber: number,
  ) => {
    setPagination({ ...pagination, page: pageNumber });
  };

  const clearSelection = async () => {
    setSelectedItems([]);
    resetSelectionStrategy();
    await dispatch(unCheckAll());
  };

  const handleRequestSort = (sorting: unknown) => {
    resetSelectionStrategy();
    dispatch(sortDataset(sorting as Parameters<typeof sortDataset>[0]));
  };

  const handleOnRefreshButtonClick = () => {
    initialize();
  };

  const handleDataFilterChange = useDebounce(
    (event: { searchText?: string }) => {
      if (searchText !== event.searchText) {
        setSearchText(event.searchText);

        if (selectedItems.length) {
          clearSelection();
        }

        if (isNotEmptyString(event.searchText ?? "")) {
          resetSelectionStrategy(RowSelectionStrategy.DISABLED);
        } else {
          resetSelectionStrategy(RowSelectionStrategy.BY_PAGE);
        }

        if (pagination.page > 0) {
          setPagination({ ...pagination, page: 0 });
        }
      }
    },
    400,
  );

  const labelCountSelection = (selection: StopContactRow[]): string => {
    return Array.isArray(selection) && selection.length > 0
      ? ` (${selection.length})`
      : "";
  };

  if (loadingStatus === "pending") {
    return <SbuxLoader />;
  }

  return (
    <Box sx={FLEX_COL_FILL}>
      <SbuxEnhancedTable
        rows={dataset}
        columns={
          columns as unknown as {
            id: string;
            label: string;
            [key: string]: unknown;
          }[]
        }
        currentPage={pagination.page}
        defaultSortingField={"initiationTimestamp"}
        defaultSortOrder={"asc"}
        RowSource={EditStopContactsRow}
        showFooter={true}
        showSearchBar={true}
        showRefreshButton
        isManager={true}
        selectionStrategy={
          selectionStrategy as unknown as Record<string, unknown>
        }
        showAddNewButton
        handleAddNewButtonClick={handleDelete}
        isdisableAddNewButton={
          dataset.length === 0 || selectedItems.length === 0
        }
        addNewButtonTitle={
          <SbuxTranslate>{`Delete${labelCountSelection(selectedItems)}`}</SbuxTranslate>
        }
        handleEditRowClick={handleEditRowClick}
        handleCheckboxChange={
          handleSelectAllChange as (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => void
        }
        handleRefreshClick={handleOnRefreshButtonClick}
        onRowsPerPageChange={
          handleRowsPerPageChange as (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => void
        }
        onPageChange={
          handlePageChange as (event: unknown, newPage: number) => void
        }
        onRequestSort={handleRequestSort}
        onDataFilterChange={handleDataFilterChange}
        footerMarginBottom={"0"}
      />
    </Box>
  );
};

export default StopContactTable;
